import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Building2, ArrowRight, Loader2,
  Settings, Search, AlertCircle, LogOut, MapPin,
  Zap, RefreshCw, Activity, LayoutGrid, Briefcase,
  TrendingUp, Wrench, Database, Cpu, DollarSign,
  BarChart3, PieChart, ShieldCheck, ChevronRight,
  Sparkles, Layers, ArrowUpRight, BookOpen
} from "lucide-react";
import "../../styles/selection-topbar.css";
import "../../styles/site-selection.css";
import "../../components/header/styles.css";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../components/ThemeContext";
import backendApi from "../../services/backendApi";
import { solarLiveApi } from "../../services/solarLiveApi";
import UserMenu from "../../components/header/UserMenu";

// ─── CDN URLs (no npm leaflet import needed) ─────────────────────────────────
const L_CSS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
const L_JS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";

// ─── Types ────────────────────────────────────────────────────────────────────
interface Site {
  site_id: number;
  site_name: string;
  site_code: string;
  company_id?: number | string | null;
  company_name?: string | null;
  // Static (blue) — from site_master
  site_type?: string | null;
  plant_code?: number | null;
  location?: string | null;
  capacity_mw?: number | string | null;
  capacity_mwp?: number | string | null;
  ppa_tariff?: number | string | null;
  plant_commissioning_date?: string | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
  is_active: boolean;
  // Dynamic (yellow) — from generation reports / live telemetry
  today_energy_kwh?: number | null;
  today_energy_mwh?: number | null;
  today_input_gii?: number | null;
  today_input_mwh?: number | null;
  peak_power_kw?: number | null;
  peak_power_mw?: number | null;
  plf_percent?: number | null;
  pr_percent?: number | null;
  _dynamicLoading?: boolean;
}

// ─── Constants ────────────────────────────────────────────────────────────────
const PLANT_LABELS: Record<number, string> = {
  0: "Solar", 1: "Wind", 2: "Hybrid", 3: "Battery-Hydro", 4: "Battery-Chem",
};
const PLANT_COLORS: Record<number, string> = {
  0: "#f59e0b", 1: "#10b981", 2: "#1C4D88", 3: "#0084CF", 4: "#ef4444",
};
const PLANT_BG: Record<number, string> = {
  0: "bg-amber-500", 1: "bg-emerald-500", 2: "bg-[#1C4D88]",
  3: "bg-[#0084CF]", 4: "bg-red-500",
};

const DYNAMIC_CACHE_TTL_MS = 60 * 1000;
const DYNAMIC_REFETCH_COOLDOWN_MS = 45 * 1000;

type DynamicSnapshot = Partial<Pick<Site, 'peak_power_kw' | 'peak_power_mw' | 'today_energy_kwh' | 'today_energy_mwh' | 'today_input_gii' | 'today_input_mwh' | 'plf_percent' | 'pr_percent'>>;

const dynamicSnapshotCache = new Map<string, { expiresAt: number; data: DynamicSnapshot }>();
const dynamicSnapshotInFlight = new Map<string, Promise<DynamicSnapshot | null>>();

// ─── Helpers ─────────────────────────────────────────────────────────────────
const getPlantLabel = (s: Site) =>
  s.plant_code != null ? (PLANT_LABELS[s.plant_code] ?? s.site_type ?? "—") : (s.site_type ?? "—");

const getPlantColor = (s: Site) =>
  s.plant_code != null ? (PLANT_COLORS[s.plant_code] ?? "#0084CF") : "#0084CF";

const getPlantBg = (s: Site) =>
  s.plant_code != null ? (PLANT_BG[s.plant_code] ?? "bg-[#0084CF]") : "bg-[#0084CF]";

const hasValidCoordinates = (s: Site): boolean => {
  if (s.latitude == null || s.longitude == null) return false;
  const lat = parseFloat(String(s.latitude));
  const lng = parseFloat(String(s.longitude));
  return Number.isFinite(lat) && Number.isFinite(lng);
};

function fmtNum(v: any, dp = 2, unit = ""): string {
  if (v == null || v === "" || v === "null" || v === "undefined") return "—";
  const n = typeof v === "string" ? parseFloat(v) : Number(v);
  if (!isFinite(n)) return "—";
  const formatted = n.toLocaleString("en-US", {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  });
  return unit ? `${formatted} ${unit}` : formatted;
}

function fmtDate(v: string | null | undefined): string {
  if (!v) return "—";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return v;
    const day = d.getDate().toString().padStart(2, "0");
    const month = d.toLocaleString("en-US", { month: "short" });
    const year = d.getFullYear().toString().slice(-2);
    return `${day} ${month} ${year}`;
  } catch {
    return v;
  }
}

function fmtPct(v: number | null | undefined, dp = 2): string {
  if (v == null) return "—";
  const n = typeof v === "string" ? parseFloat(v) : Number(v);
  if (!isFinite(n)) return "—";
  return `${n.toFixed(dp)}%`;
}

async function fetchDynamicSnapshot(siteCode: string, today: string, acCapacityMw?: number): Promise<DynamicSnapshot | null> {
  const cacheKey = `${siteCode.toUpperCase()}|${today}`;
  const cached = dynamicSnapshotCache.get(cacheKey);
  if (cached && Date.now() < cached.expiresAt) {
    return cached.data;
  }

  const inFlight = dynamicSnapshotInFlight.get(cacheKey);
  if (inFlight) {
    return inFlight;
  }

  const request = (async () => {
    try {
      const kpis = await solarLiveApi.getKpiCards({ plant_id: siteCode });
      if (kpis && kpis.success && kpis.cards) {
        const peakKw = kpis.cards.peak_active_power?.value ?? 0;
        const exportKwh = kpis.cards.export_energy?.value ?? 0;
        const gii = kpis.cards.solar_gii?.value ?? null;
        const pr = kpis.cards.instantaneous_pr?.value ?? null;
        const capMw = acCapacityMw || (kpis.dc_capacity_mwp ? kpis.dc_capacity_mwp / 1.22 : 2.52);
        const plf = capMw > 0 && exportKwh > 0 ? (exportKwh / (capMw * 24.0 * 1000.0)) * 100.0 : null;

        const snapshot: DynamicSnapshot = {
          peak_power_kw: peakKw > 0 ? peakKw : null,
          peak_power_mw: peakKw > 0 ? Number((peakKw / 1000.0).toFixed(3)) : null,
          today_energy_kwh: exportKwh > 0 ? exportKwh : null,
          today_energy_mwh: exportKwh > 0 ? Number((exportKwh / 1000.0).toFixed(3)) : null,
          today_input_gii: gii != null ? Number(gii) : null,
          today_input_mwh: gii != null ? Number(gii) : null,
          plf_percent: plf != null ? Number(plf.toFixed(2)) : null,
          pr_percent: pr != null ? Number(pr) : null,
        };

        dynamicSnapshotCache.set(cacheKey, {
          data: snapshot,
          expiresAt: Date.now() + DYNAMIC_CACHE_TTL_MS,
        });

        return snapshot;
      }
    } catch (err) {
      console.warn(`[SiteSelection] Error fetching live telemetry for ${siteCode}:`, err);
    }
    return null;
  })();

  dynamicSnapshotInFlight.set(cacheKey, request);
  try {
    return await request;
  } finally {
    dynamicSnapshotInFlight.delete(cacheKey);
  }
}

// Load CSS link once
function injectCSS(href: string) {
  if (!document.querySelector(`link[href="${href}"]`)) {
    const l = document.createElement("link");
    l.rel = "stylesheet"; l.href = href;
    document.head.appendChild(l);
  }
}

// Load JS script once, returns promise
function loadScript(src: string): Promise<void> {
  return new Promise((res, rej) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if ((window as any).L) {
        res();
      } else {
        existing.addEventListener("load", () => res());
        existing.addEventListener("error", (e) => rej(e));
      }
      return;
    }
    const s = document.createElement("script");
    s.src = src; s.async = true;
    s.onload = () => res();
    s.onerror = () => rej(new Error(`Cannot load ${src}`));
    document.head.appendChild(s);
  });
}

// ─── Table column config ──────────────────────────────────────────────────────
type ColKind = "static" | "dynamic";
interface Col {
  key: string;
  header: string;
  kind: ColKind;
  minW: number;
  render: (s: Site) => React.ReactNode;
}

function buildColumns(dm: boolean): Col[] {
  const dyn = (node: React.ReactNode, loading?: boolean): React.ReactNode =>
    loading ? <span className={`text-xs ${dm ? "text-gray-600" : "text-gray-300"}`}>···</span> : node;

  return [
    {
      key: "power_plant", header: "Power Plant", kind: "static", minW: 130,
      render: (s) => (
        <div className="flex items-center gap-2 min-w-0">
          <span className={`power-plant-name text-sm truncate leading-tight ${dm ? "text-[#3EA3DA]" : "text-[#0084CF]"}`} title={s.site_name}>
            {s.site_name}
          </span>
        </div>
      ),
    },
    {
      key: "type", header: "Type", kind: "static", minW: 70,
      render: (s) => {
        const label = getPlantLabel(s);
        const colorMap: Record<string, string> = {
          Solar: "bg-amber-100 text-amber-700",
          Wind: "bg-emerald-100 text-emerald-700",
          Hybrid: "bg-[#E3F0F9] text-[#1C4D88]",
        };
        const cls = colorMap[label] ?? "bg-gray-100 text-gray-600";
        return <span className={`plant-type-badge px-1.5 py-0.5 rounded text-[12px] whitespace-nowrap ${cls}`}>{label}</span>;
      },
    },
    {
      key: "location", header: "Location", kind: "static", minW: 95,
      render: (s) => (
        <div className="flex items-center gap-1 min-w-0">
          <MapPin size={10} className={`flex-shrink-0 ${dm ? "text-gray-500" : "text-gray-400"}`} />
          <span className={`text-xs truncate ${dm ? "text-gray-300" : "text-gray-600"}`} title={s.location ?? ""}>
            {s.location ?? "—"}
          </span>
        </div>
      ),
    },
    {
      key: "capacity_mwp", header: "DC Capacity", kind: "static", minW: 78,
      render: (s) => <span className="text-xs">{fmtNum(s.capacity_mwp, 2)}</span>,
    },
    {
      key: "capacity_mw", header: "AC Capacity", kind: "static", minW: 78,
      render: (s) => <span className="text-xs">{fmtNum(s.capacity_mw, 2)}</span>,
    },
    {
      key: "ppa_tariff", header: "Tariff", kind: "static", minW: 72,
      render: (s) => <span className="text-xs">{fmtNum(s.ppa_tariff, 2)}</span>,
    },
    {
      key: "cod", header: "COD", kind: "static", minW: 90,
      render: (s) => <span className="text-xs">{fmtDate(s.plant_commissioning_date)}</span>,
    },
    // ── Dynamic (yellow)
    {
      key: "peak_power", header: "Peak Power", kind: "dynamic", minW: 86,
      render: (s) => dyn(
        <b
          className="axs-num"
          style={{
            fontWeight: 600,
            color: dm ? "#8AC4E6" : "#0D2747",
            textShadow: dm ? "none" : "0.2px 0 0 #0D2747, -0.2px 0 0 #0D2747",
            display: "inline-block",
          }}>
          {fmtNum(s.peak_power_kw ?? (s.peak_power_mw ? s.peak_power_mw * 1000 : null), 2)}
        </b>, s._dynamicLoading,
      ),
    },
    {
      key: "today_energy", header: "Today Energy", kind: "dynamic", minW: 90,
      render: (s) => dyn(
        <b
          className="axs-num"
          style={{
            fontWeight: 600,
            color: dm ? "#8AC4E6" : "#0D2747",
            textShadow: dm ? "none" : "0.2px 0 0 #0D2747, -0.2px 0 0 #0D2747",
            display: "inline-block",
          }}>
          {fmtNum(s.today_energy_kwh ?? (s.today_energy_mwh ? s.today_energy_mwh * 1000 : null), 2)}
        </b>, s._dynamicLoading,
      ),
    },
    {
      key: "today_input", header: "Today Input", kind: "dynamic", minW: 80,
      render: (s) => dyn(
        <b
          className="axs-num"
          style={{
            fontWeight: 600,
            color: dm ? "#8AC4E6" : "#0D2747",
            textShadow: dm ? "none" : "0.2px 0 0 #0D2747, -0.2px 0 0 #0D2747",
            display: "inline-block",
          }}>
          {fmtNum(s.today_input_gii ?? s.today_input_mwh, 2)}
        </b>, s._dynamicLoading,
      ),
    },
    {
      key: "plf", header: "PLF", kind: "dynamic", minW: 60,
      render: (s) => dyn(
        <b
          className="axs-num"
          style={{
            fontWeight: 600,
            color: dm ? "#8AC4E6" : "#0D2747",
            textShadow: dm ? "none" : "0.2px 0 0 #0D2747, -0.2px 0 0 #0D2747",
            display: "inline-block",
          }}>
          {fmtPct(s.plf_percent, 2)}
        </b>, s._dynamicLoading,
      ),
    },
    {
      key: "pr", header: "PR", kind: "dynamic", minW: 60,
      render: (s) => dyn(
        <b
          className="axs-num"
          style={{
            fontWeight: 600,
            color: dm ? "#8AC4E6" : "#0D2747",
            textShadow: dm ? "none" : "0.2px 0 0 #0D2747, -0.2px 0 0 #0D2747",
            display: "inline-block",
          }}>
          {fmtPct(s.pr_percent, 2)}
        </b>, s._dynamicLoading,
      ),
    },
  ];
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const SiteSelection: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, login, logout } = useAuth();

  const { isDarkMode } = useTheme();
  const isDark = isDarkMode;
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQ, setSearchQ] = useState("");
  const [activeSite, setActiveSite] = useState<number | null>(null); // highlighted in table
  const [leafletOk, setLeafletOk] = useState(false);
  const [viewMode, setViewMode] = useState<"sites" | "portfolio">("sites");
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!userMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        const portal = document.querySelector('.user-dropdown-menu');
        if (portal && portal.contains(e.target as Node)) return;
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [userMenuOpen]);

  const portfolioMetrics = useMemo(() => {
    let totalDc = 0;
    let totalAc = 0;
    let totalTodayEnergy = 0;
    let totalPeakPower = 0;
    let plfSum = 0, plfCount = 0;
    let prSum = 0, prCount = 0;
    let totalTariffSum = 0, tariffCount = 0;

    sites.forEach((s) => {
      const dc = parseFloat(String(s.capacity_mwp || 0));
      const ac = parseFloat(String(s.capacity_mw || 0));
      const energy = parseFloat(String(s.today_energy_mwh || 0));
      const peak = parseFloat(String(s.peak_power_mw || 0));
      const plf = parseFloat(String(s.plf_percent || 0));
      const pr = parseFloat(String(s.pr_percent || 0));
      const tariff = parseFloat(String(s.ppa_tariff || 0));

      if (!isNaN(dc)) totalDc += dc;
      if (!isNaN(ac)) totalAc += ac;
      if (!isNaN(energy)) totalTodayEnergy += energy;
      if (!isNaN(peak)) totalPeakPower += peak;
      if (!isNaN(plf) && plf > 0) { plfSum += plf; plfCount++; }
      if (!isNaN(pr) && pr > 0) { prSum += pr; prCount++; }
      if (!isNaN(tariff) && tariff > 0) { totalTariffSum += tariff; tariffCount++; }
    });

    const avgPlf = plfCount > 0 ? plfSum / plfCount : 0;
    const avgPr = prCount > 0 ? prSum / prCount : 0;
    const avgTariff = tariffCount > 0 ? totalTariffSum / tariffCount : 0;
    const estTodayRevenue = totalTodayEnergy * 1000 * (avgTariff || 4.25);

    return {
      totalDc,
      totalAc,
      totalTodayEnergy,
      totalPeakPower,
      avgPlf,
      avgPr,
      avgTariff,
      estTodayRevenue,
      totalSites: sites.length,
    };
  }, [sites]);

  const companyId = useMemo(() => {
    const qCompany = searchParams.get("company_id");
    const storedComp = sessionStorage.getItem("company_id") || localStorage.getItem("company_id");
    return qCompany || storedComp || (user?.companyId ? String(user.companyId) : null);
  }, [searchParams, user?.companyId]);



  const mapDivRef = useRef<HTMLDivElement>(null);
  const leafletMap = useRef<any>(null);
  const tileLayerRef = useRef<any>(null);
  const tileErrorCountRef = useRef(0);
  const usingFallbackTilesRef = useRef(false);
  const markersMap = useRef<Map<number, any>>(new Map());
  const tbodyRef = useRef<HTMLTableSectionElement>(null);
  const initialViewRef = useRef(false);
  const mountedRef = useRef(true);
  const loadSitesInFlightRef = useRef(false);
  const hasLoadedSitesRef = useRef(false);
  const sitesRef = useRef<Site[]>([]);
  const dynamicFetchGuardRef = useRef<{ signature: string; at: number }>({ signature: "", at: 0 });

  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);
  useEffect(() => { sitesRef.current = sites; }, [sites]);


  // ── Load Leaflet CSS + JS once
  useEffect(() => {
    injectCSS(L_CSS);
    loadScript(L_JS).then(() => { if (mountedRef.current) setLeafletOk(true); }).catch(console.warn);
  }, []);

  // ── Fetch dynamic data per site (live telemetry / company KPIs)
  const loadDynamic = useCallback(async (siteList: Site[]) => {
    const today = new Date().toISOString().split("T")[0];
    const signature = `${today}|${siteList.map((s) => s.site_id).sort((a, b) => a - b).join(",")}`;
    const now = Date.now();

    if (
      dynamicFetchGuardRef.current.signature === signature &&
      now - dynamicFetchGuardRef.current.at < DYNAMIC_REFETCH_COOLDOWN_MS
    ) {
      setSites((prev) => prev.map((s) => ({ ...s, _dynamicLoading: false })));
      return;
    }

    dynamicFetchGuardRef.current = { signature, at: now };

    const updates: Record<number, Partial<Site>> = {};
    const activeCompId = companyId || searchParams.get("company_id");
    const batchFetchedCodes = new Set<string>();

    // 1. Attempt single batch fetch for company sites
    if (activeCompId) {
      try {
        const batchRes = await solarLiveApi.getCompanyKpis({ company_id: activeCompId });
        if (batchRes && batchRes.success && batchRes.sites) {
          siteList.forEach((site) => {
            const match = batchRes.sites[site.site_code?.toUpperCase()];
            if (match) {
              updates[site.site_id] = {
                peak_power_kw: match.peak_power_kw,
                peak_power_mw: match.peak_power_mw,
                today_energy_kwh: match.today_energy_kwh,
                today_energy_mwh: match.today_energy_mwh,
                today_input_gii: match.today_input_gii,
                today_input_mwh: match.today_input_mwh,
                plf_percent: match.plf_percent,
                pr_percent: match.pr_percent,
              };
              batchFetchedCodes.add(site.site_code.toUpperCase());
            }
          });
        }
      } catch (err) {
        console.warn("[SiteSelection] Batch company KPI fetch error, fallback to per-site:", err);
      }
    }

    // 2. Fetch any remaining sites individually with concurrency control
    const remaining = siteList.filter((s) => !batchFetchedCodes.has(s.site_code?.toUpperCase())).slice(0, 15);
    const CONCURRENCY = 3;
    for (let i = 0; i < remaining.length; i += CONCURRENCY) {
      const batch = remaining.slice(i, i + CONCURRENCY);
      await Promise.all(
        batch.map(async (site) => {
          try {
            const snapshot = await fetchDynamicSnapshot(
              site.site_code,
              today,
              site.capacity_mw ? parseFloat(String(site.capacity_mw)) : undefined
            );
            if (snapshot) {
              updates[site.site_id] = snapshot;
            }
          } catch {
            // Skip on individual errors
          }
        })
      );
      if (i + CONCURRENCY < remaining.length) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    }

    if (!mountedRef.current) return;
    setSites((prev) => prev.map((s) => ({ ...s, _dynamicLoading: false, ...(updates[s.site_id] ?? {}) })));
  }, [companyId, searchParams]);

  // ── Fetch sites
  const loadSites = useCallback(async () => {
    if (loadSitesInFlightRef.current) return;
    loadSitesInFlightRef.current = true;
    const isInitialLoad = !hasLoadedSitesRef.current;
    if (isInitialLoad) {
      setLoading(true);
    }
    setError("");
    try {
      const qCompany = searchParams.get("company_id");
      const storedComp = sessionStorage.getItem("company_id") || localStorage.getItem("company_id");
      const role = (user?.roleName || user?.role || "").toLowerCase();
      const isSysAdmin = !!user?.isGlobalAdmin || role === "system_admin" || role === "super_admin";
      const resolvedCompanyId = qCompany || storedComp || (user?.companyId ? String(user.companyId) : null);

      if (resolvedCompanyId) {
        sessionStorage.setItem("company_id", resolvedCompanyId);
        localStorage.setItem("company_id", resolvedCompanyId);
      }
      if (isSysAdmin && !resolvedCompanyId) { navigate("/select-company", { replace: true }); return; }

      const res = await backendApi.getSites(false, "", resolvedCompanyId);
      if (!mountedRef.current) return;

      if (res.success && res.sites) {
        const prevById = new Map(sitesRef.current.map((s) => [s.site_id, s]));
        const active: Site[] = (res.sites as any[])
          .filter((s: any) => s.is_active !== false)
          .map((s: any) => {
            const prev = prevById.get(s.site_id);
            if (!prev) {
              return { ...s, _dynamicLoading: true };
            }
            return {
              ...s,
              peak_power_kw: prev.peak_power_kw,
              peak_power_mw: prev.peak_power_mw,
              today_energy_kwh: prev.today_energy_kwh,
              today_energy_mwh: prev.today_energy_mwh,
              today_input_gii: prev.today_input_gii,
              today_input_mwh: prev.today_input_mwh,
              plf_percent: prev.plf_percent,
              pr_percent: prev.pr_percent,
              _dynamicLoading: false,
            };
          });

        setSites(active);
        hasLoadedSitesRef.current = true;

        const needsDynamic = active.filter((s) =>
          s.peak_power_kw == null &&
          s.peak_power_mw == null &&
          s.today_energy_kwh == null &&
          s.today_energy_mwh == null &&
          s.today_input_gii == null &&
          s.today_input_mwh == null &&
          s.plf_percent == null &&
          s.pr_percent == null
        );

        if (needsDynamic.length > 0) {
          loadDynamic(needsDynamic);
        }
      } else {
        setError("Failed to load sites. Please try again.");
      }
    } catch {
      if (mountedRef.current) setError("An error occurred while loading sites.");
    } finally {
      loadSitesInFlightRef.current = false;
      if (mountedRef.current && isInitialLoad) setLoading(false);
    }
  }, [searchParams.toString(), user?.companyId, user?.role, user?.roleName, user?.isGlobalAdmin, loadDynamic]);

  useEffect(() => { loadSites(); }, [loadSites]);

  // ── Build / refresh Leaflet map
  useEffect(() => {
    if (!leafletOk || !mapDivRef.current || loading) return;
    const L = (window as any).L;
    if (!L) return;

    if (!leafletMap.current) {
      leafletMap.current = L.map(mapDivRef.current, { zoomControl: true, scrollWheelZoom: true, attributionControl: false });

      // Google Maps Roadmap tiles
      const primaryLayer = L.tileLayer("https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}", {
        maxZoom: 20,
        subdomains: ["mt0", "mt1", "mt2", "mt3"],
        attribution: "Map data &copy; Google",
      });

      tileLayerRef.current = primaryLayer.addTo(leafletMap.current);

      // Ensure tiles render correctly after layout has settled.
      setTimeout(() => {
        leafletMap.current?.invalidateSize();
      }, 200);
      requestAnimationFrame(() => {
        setTimeout(() => leafletMap.current?.invalidateSize(), 50);
      });
    }

    const map = leafletMap.current;
    markersMap.current.forEach((m) => m.remove());
    markersMap.current.clear();

    const geo = sites.filter(hasValidCoordinates);

    if (!geo.length) { map.setView([20.5937, 78.9629], 4); return; }

    const bounds: [number, number][] = [];
    geo.forEach((site) => {
      const lat = parseFloat(String(site.latitude));
      const lng = parseFloat(String(site.longitude));
      const col = getPlantColor(site);

      const iconHtml = `
        <div style="width:32px;height:32px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);
          background:${col};border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.3);
          display:flex;align-items:center;justify-content:center;">
          <span style="transform:rotate(45deg);font-size:12px;color:#fff;">⚡</span>
        </div>`;

      const popupHtml = `
        <div style="font-family:'IBM Plex Sans',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;min-width:170px;padding:2px 0">
          <div style="font-weight:800;font-size:13px;color:#0084CF;line-height:1.2">${site.site_name}</div>
          <div style="font-size:11px;color:#64748b;margin:2px 0 6px 0">${getPlantLabel(site)}</div>
          <div style="font-size:11px;color:#334155;display:grid;gap:2px">
            <div><b>Location:</b> ${site.location ?? "—"}</div>
            <div><b>AC/DC:</b> ${fmtNum(site.capacity_mw, 2, "MW")} / ${fmtNum(site.capacity_mwp, 2, "MWp")}</div>
            <div><b>COD:</b> ${fmtDate(site.plant_commissioning_date)}</div>
          </div>
        </div>`;

      const icon = L.divIcon({ html: iconHtml, className: "", iconSize: [32, 32], iconAnchor: [16, 32] });
      const marker = L.marker([lat, lng], { icon })
        .addTo(map)
        .bindPopup(popupHtml, { maxWidth: 230 });

      marker.on("click", () => {
        setActiveSite(site.site_id);
        // scroll table row into view
        setTimeout(() => {
          const row = tbodyRef.current?.querySelector(`[data-sid="${site.site_id}"]`);
          row?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }, 50);
      });

      markersMap.current.set(site.site_id, marker);
      bounds.push([lat, lng]);
    });

    if (!initialViewRef.current) {
      map.setView([20.5937, 78.9629], 4);
      initialViewRef.current = true;
    } else if (activeSite == null && bounds.length > 0) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 10 });
    }
  }, [leafletOk, sites, loading, activeSite]);

  // Keep map tiles rendered when panel/window size changes.
  useEffect(() => {
    if (!leafletOk || !leafletMap.current || !mapDivRef.current) return;

    const map = leafletMap.current;
    const onResize = () => map.invalidateSize();
    window.addEventListener("resize", onResize);

    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(() => map.invalidateSize());
      observer.observe(mapDivRef.current);
    }

    return () => {
      window.removeEventListener("resize", onResize);
      observer?.disconnect();
    };
  }, [leafletOk]);

  // Invalidate map size when switching back to Sites View tab
  useEffect(() => {
    if (viewMode === "sites" && leafletMap.current) {
      const timer1 = setTimeout(() => leafletMap.current?.invalidateSize(), 50);
      const timer2 = setTimeout(() => leafletMap.current?.invalidateSize(), 250);
      return () => { clearTimeout(timer1); clearTimeout(timer2); };
    }
  }, [viewMode]);

  // ── Pan to site on row highlight
  useEffect(() => {
    if (activeSite == null) return;
    const marker = markersMap.current.get(activeSite);
    if (marker && leafletMap.current) {
      leafletMap.current.panTo(marker.getLatLng(), { animate: true, duration: 0.5 });
      marker.openPopup();
    }
  }, [activeSite]);

  // ── Navigate into site
  const openSite = (site: Site) => {
    sessionStorage.setItem("site_id", String(site.site_id));
    localStorage.setItem("site_id", String(site.site_id));
    sessionStorage.setItem("tenant", site.site_code);
    sessionStorage.setItem("tenant_name", site.site_name);
    if (site.company_id != null) {
      sessionStorage.setItem("company_id", String(site.company_id));
      localStorage.setItem("company_id", String(site.company_id));
    }
    if (site.company_name) {
      sessionStorage.setItem("selected_company_name", site.company_name);
      localStorage.setItem("selected_company_name", site.company_name);
    }
    localStorage.setItem("currentSite", site.site_code);
    if (user) {
      login({
        ...user,
        siteId: site.site_id, siteCode: site.site_code,
        tenant: site.site_code, tenantName: site.site_name,
        companyId: site.company_id ?? user.companyId,
        company: site.company_name || user.company,
      });
    }
    navigate(`/dashboard/${site.site_code}`);
  };

  // ── Auto-navigate for general users who should directly enter their assigned site
  useEffect(() => {
    if (loading) return;
    const role = (user?.roleName || user?.role || "").toLowerCase();
    const isGeneralUser = role === "general_user" || role === "user";
    if (!isGeneralUser) return;

    if (sites.length > 0) {
      const targetSite = (user?.siteCode ? sites.find((s) => s.site_code?.toUpperCase() === user.siteCode?.toUpperCase()) : null) || sites[0];
      if (targetSite) {
        openSite(targetSite);
      }
    } else if (user?.siteCode) {
      navigate(`/dashboard/${user.siteCode}`, { replace: true });
    }
  }, [loading, sites, user?.role, user?.roleName, user?.siteCode]);

  const filtered = useMemo(() =>
    sites.filter((s) =>
      !searchQ ||
      s.site_name?.toLowerCase().includes(searchQ.toLowerCase()) ||
      s.site_code?.toLowerCase().includes(searchQ.toLowerCase()) ||
      s.location?.toLowerCase().includes(searchQ.toLowerCase()) ||
      s.company_name?.toLowerCase().includes(searchQ.toLowerCase())
    ),
    [sites, searchQ]
  );

  const cols = useMemo(() => buildColumns(isDark), [isDark]);
  const dm = isDark;

  // ── Role helpers
  const roleStr = (user?.roleName || user?.role || "").toLowerCase();
  const isSysAdmin = !!user?.isGlobalAdmin || ["system_admin", "super_admin"].includes(roleStr);
  const isAdmin = isSysAdmin || ["ho_admin", "company_admin", "site_admin", "ho", "admin"].includes(roleStr);
  const mappedCount = sites.filter(hasValidCoordinates).length;

  // ─────────────────────────────────────────────────────────────────
  // LOADING
  // ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className={`ibm-plex-page h-screen flex flex-col ${dm ? "bg-gray-900" : "bg-[#F6F8FB]"}`}>
        <div className={`flex-shrink-0 h-14 border-b flex items-center px-6 gap-3 ${dm ? "bg-[#12263F] border-[#1C4D88]" : "bg-white border-[#DFE6EE]"}`}>
          <Building2 size={22} className="text-[#0084CF]" />
          <span className={`font-bold text-base ${dm ? "text-white" : "text-gray-900"}`}>AxonMetric</span>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 size={42} className="animate-spin text-[#0084CF] mx-auto mb-4" />
            <p className={`font-semibold ${dm ? "text-gray-300" : "text-gray-600"}`}>Loading sites…</p>
          </div>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────
  // MAIN RENDER
  // ─────────────────────────────────────────────────────────────────
  return (
    <div className={`axs ibm-plex-page h-screen flex flex-col overflow-hidden transition-colors duration-200 ${dm ? "bg-gray-900 text-white" : "bg-[#EEF2F7] text-gray-900"}`}>
      <style dangerouslySetInnerHTML={{ __html: `
        .power-plant-name, .power-plant-btn {
          color: ${dm ? "#3EA3DA" : "#0084CF"} !important;
        }
      ` }} />

      {/* ── TOPBAR ─────────────────────────────────────────────────── */}
      <header
        className={`flex-shrink-0 z-50 border-b shadow-sm flex items-center justify-between pl-2 pr-5 ${dm ? "bg-[#12263F] border-[#1C4D88]" : "bg-white border-[#DFE6EE]"}`}
        style={{ height: 54 }}
      >
        {/* Left */}
        <div className="flex items-center gap-4">
          <img
            src="/Axon-logo.png"
            alt="AxonMetric Logo"
            className="h-8 sm:h-10 hover:scale-105 transition-transform duration-200 cursor-pointer"
            onClick={() => navigate('/')}
          />
          {/* <div className={`hidden sm:block border-l pl-4 ${dm ? 'border-gray-700' : 'border-[#DFE6EE]'}`}>
            <p className={`text-[10px] font-bold uppercase tracking-widest ${dm ? 'text-gray-400' : 'text-gray-500'}`}>Site Selection</p>
          </div> */}
        </div>

        {/* Right */}
        <div className="axm-topbar flex items-center gap-2">
          {/* Search */}
          <div className={`axm-search relative flex-shrink-0 w-64 rounded-lg border transition-colors ${dm ? "border-[#2F5C8A] bg-[#12263F]" : "border-[#C5D0DC] bg-white"}`}>
            <Search size={13} className={`axm-search__icon absolute left-3 top-1/2 -translate-y-1/2 ${dm ? "text-[#6E8CAD]" : "text-[#9AA8B8]"}`} />
            <input type="text" placeholder="Search sites…" value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              className={`w-full pl-8 pr-7 py-2 text-xs rounded-lg outline-none bg-transparent ${dm ? "text-white placeholder-[#6E8CAD]" : "text-[#16202B] placeholder-[#9AA8B8]"}`} />
            {searchQ && (
              <button onClick={() => setSearchQ("")}
                aria-label="Clear search"
                className={`absolute right-2 top-1/2 -translate-y-1/2 text-xs transition-colors ${dm ? "text-[#6E8CAD] hover:text-white" : "text-[#9AA8B8] hover:text-[#0066A3]"}`}>✕</button>
            )}
          </div>
          {isSysAdmin && (
            <button onClick={() => navigate("/select-company")}
              className={`axm-topbtn inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs transition-colors border ${dm ? "border-[#2F5C8A] bg-transparent hover:bg-[#0084CF]/15 hover:border-[#0084CF] text-[#C2DFF1]" : "border-[#C5D0DC] bg-white hover:bg-[#F2F8FC] hover:border-[#0084CF] text-[#1C4D88]"}`}>
              <ArrowRight size={13} strokeWidth={2.2} className="rotate-180" /> <span>Companies</span>
            </button>
          )}
          <button type="button" onClick={() => navigate("/guide")} title="Where to go and what to click"
            className={`axm-topbtn inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs transition-colors border ${dm ? "border-[#2F5C8A] bg-transparent hover:bg-[#0084CF]/15 hover:border-[#0084CF] text-[#C2DFF1]" : "border-[#C5D0DC] bg-white hover:bg-[#F2F8FC] hover:border-[#0084CF] text-[#1C4D88]"}`}>
            <BookOpen size={13} strokeWidth={2.2} aria-hidden="true" /> <span>Guide</span>
          </button>
          {isAdmin && (
            <button onClick={() => navigate("/admin")}
              className={`axm-topbtn inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs transition-colors border ${dm ? "border-[#2F5C8A] bg-transparent hover:bg-[#0084CF]/15 hover:border-[#0084CF] text-[#C2DFF1]" : "border-[#C5D0DC] bg-white hover:bg-[#F2F8FC] hover:border-[#0084CF] text-[#1C4D88]"}`}>
              <Settings size={13} strokeWidth={2.2} /> <span>Admin</span>
            </button>
          )}
          <div ref={userMenuRef} style={{ position: 'relative', zIndex: 10001 }}>
            <UserMenu
              displayName={user?.fullName || user?.username || 'User'}
              displayRole={user?.roleName || user?.role || 'User'}
              site=""
              showSiteNavigation={false}
              menuOpen={userMenuOpen}
              onToggleMenu={() => setUserMenuOpen(v => !v)}
              onSiteInfo={() => {
                setUserMenuOpen(false);
                navigate('/select-site');
              }}
              onLogout={() => {
                setUserMenuOpen(false);
                logout?.();
                navigate('/login');
              }}
            />
          </div>
        </div>
      </header>



      {/* ── ERROR ──────────────────────────────────────────────────── */}
      <AnimatePresence>
        {error && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className={`flex-shrink-0 flex items-center gap-3 px-5 py-2.5 border-b ${dm ? "bg-red-900/20 border-red-800 text-red-300" : "bg-red-50 border-red-200 text-red-700"}`}>
            <AlertCircle size={15} className="flex-shrink-0" />
            <span className="text-sm font-medium flex-1">{error}</span>
            <button onClick={loadSites}
              className={`text-xs px-3 py-1 rounded font-semibold ${dm ? "bg-red-800/40 hover:bg-red-800" : "bg-red-100 hover:bg-red-200"}`}>
              Retry
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── CONTENT: Table (left 60%) + Map (right 40%) ─────────── */}
      <div className={`flex-1 flex overflow-hidden p-2 gap-2 ${dm ? "bg-gray-900" : "bg-[#EEF2F7]"}`}>

        {/* ════ TABLE / PORTFOLIO PANEL ════════════════════════════════════════ */}
        <div className={`flex flex-col border overflow-hidden rounded-xl shadow-lg transition-all duration-300 ${dm ? "border-gray-700 bg-gray-800/30 shadow-black/40" : "border-[#DFE6EE] bg-white shadow-slate-200/80"}`}
          style={{ width: viewMode === "portfolio" ? "100%" : "72%", minWidth: 0 }}>

          {/* View toggle & status bar */}
          <div className={`flex-shrink-0 flex items-center justify-between pl-3 pr-4 h-11 border-b ${dm ? "bg-gray-800 border-gray-700" : "bg-[#F6F8FB] border-[#EEF2F7]"}`}>
            <div className="flex items-center gap-3">
              {/* Segmented Switch */}
              <div data-tour="ss-views" className={`inline-flex items-center p-0.5 rounded-lg border ${dm ? "bg-gray-900/80 border-gray-700" : "bg-gray-200/80 border-gray-300"}`}>
                <button
                  type="button"
                  onClick={() => setViewMode("sites")}
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-extrabold transition-all duration-150 ${
                    viewMode === "sites"
                      ? dm
                        ? "bg-[#0084CF] text-white shadow-md shadow-[#0D2747]/30"
                        : "bg-white text-[#1C4D88] shadow-sm"
                      : dm
                      ? "text-gray-400 hover:text-white"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <LayoutGrid size={13} />
                  Sites View
                  <span className={`ml-1 px-1.5 py-0.2 rounded-full text-[10px] font-extrabold ${
                    viewMode === "sites"
                      ? dm ? "bg-[#1C4D88] text-[#C2DFF1]" : "bg-[#E3F0F9] text-[#1C4D88]"
                      : dm ? "bg-gray-800 text-gray-400" : "bg-gray-300 text-gray-700"
                  }`}>
                    {filtered.length}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setViewMode("portfolio")}
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-extrabold transition-all duration-150 ${
                    viewMode === "portfolio"
                      ? dm
                        ? "bg-[#0084CF] text-white shadow-md shadow-[#0D2747]/30"
                        : "bg-white text-[#1C4D88] shadow-sm"
                      : dm
                      ? "text-gray-400 hover:text-white"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <Briefcase size={13} />
                  Portfolio View
                </button>
              </div>
            </div>

            {viewMode === "sites" ? (
              <div className="flex items-center gap-4 text-[11px] font-semibold">
                <span className={`flex items-center gap-1 ${dm ? "text-[#3EA3DA]" : "text-[#0084CF]"}`}>
                  <Activity size={10} /> Blue = static from onboarding
                </span>
                <span className={`flex items-center gap-1 ${dm ? "text-[#8AC4E6]" : "text-[#0066A3]"}`}>
                  <Zap size={10} /> Light blue = today's live data
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-[11px] font-semibold">
                <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-extrabold ${dm ? "bg-emerald-900/40 text-emerald-400 border border-emerald-800" : "bg-emerald-50 text-emerald-700 border border-emerald-200"}`}>
                  Portfolio Active ({sites.length} Sites)
                </span>
              </div>
            )}
          </div>

          {viewMode === "portfolio" ? (
            /* PORTFOLIO VIEW CONTENT */
            <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4">
              {/* Portfolio Summary KPI Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className={`p-3.5 rounded-xl border transition-all ${dm ? "bg-gray-800/80 border-gray-700" : "bg-[#F6F8FB] border-[#DFE6EE]"}`}>
                  <div className="axs-label flex items-center justify-between text-gray-400 mb-1">
                    <span>Total Portfolio Capacity</span>
                    <Zap size={14} className="text-[#0084CF]" />
                  </div>
                  <div className={`axs-figure ${dm ? "text-white" : "text-gray-900"}`}>
                    {fmtNum(portfolioMetrics.totalDc, 2)} <span className="text-xs text-gray-400">DC MWp</span>
                  </div>
                  <div className="text-[11px] text-gray-400 mt-1 flex items-center justify-between">
                    <span>AC Capacity: {fmtNum(portfolioMetrics.totalAc, 2)} MW</span>
                    <span className="text-[#0084CF] font-bold">{portfolioMetrics.totalSites} Sites</span>
                  </div>
                </div>

                <div className={`p-3.5 rounded-xl border transition-all ${dm ? "bg-gray-800/80 border-gray-700" : "bg-[#F6F8FB] border-[#DFE6EE]"}`}>
                  <div className="axs-label flex items-center justify-between text-gray-400 mb-1">
                    <span>Today's Energy Generation</span>
                    <TrendingUp size={14} className="text-[#0084CF]" />
                  </div>
                  <div className={`axs-figure ${dm ? "text-[#8AC4E6]" : "text-[#0066A3]"}`}>
                    {fmtNum(portfolioMetrics.totalTodayEnergy, 2)} <span className="text-xs text-gray-400">MWh</span>
                  </div>
                  <div className="text-[11px] text-gray-400 mt-1 flex items-center justify-between">
                    <span>Peak Power: {fmtNum(portfolioMetrics.totalPeakPower, 2)} MW</span>
                    <span className="text-emerald-500 font-bold">Live Sync</span>
                  </div>
                </div>

                <div className={`p-3.5 rounded-xl border transition-all ${dm ? "bg-gray-800/80 border-gray-700" : "bg-[#F6F8FB] border-[#DFE6EE]"}`}>
                  <div className="axs-label flex items-center justify-between text-gray-400 mb-1">
                    <span>Fleet Efficiency (Avg)</span>
                    <Activity size={14} className="text-[#0084CF]" />
                  </div>
                  <div className={`axs-figure ${dm ? "text-[#3EA3DA]" : "text-[#0084CF]"}`}>
                    {fmtPct(portfolioMetrics.avgPr)} <span className="text-xs text-gray-400">PR</span>
                  </div>
                  <div className="text-[11px] text-gray-400 mt-1 flex items-center justify-between">
                    <span>Avg PLF: {fmtPct(portfolioMetrics.avgPlf)}</span>
                    <span className="text-[#0084CF] font-bold">Optimized</span>
                  </div>
                </div>

                <div className={`p-3.5 rounded-xl border transition-all ${dm ? "bg-gray-800/80 border-gray-700" : "bg-[#F6F8FB] border-[#DFE6EE]"}`}>
                  <div className="axs-label flex items-center justify-between text-gray-400 mb-1">
                    <span>Commercial & PPA Tariff</span>
                    <DollarSign size={14} className="text-[#1C4D88]" />
                  </div>
                  <div className={`axs-figure ${dm ? "text-[#8AC4E6]" : "text-[#1C4D88]"}`}>
                    ₹{fmtNum(portfolioMetrics.avgTariff || 4.25, 2)} <span className="text-xs text-gray-400">/kWh</span>
                  </div>
                  <div className="text-[11px] text-gray-400 mt-1 flex items-center justify-between">
                    <span>Est Today Rev: ₹{fmtNum(portfolioMetrics.estTodayRevenue, 0)}</span>
                    <span className="text-[#1C4D88] font-bold">PPA Active</span>
                  </div>
                </div>
              </div>

              {/* Section Header */}
              <div className="flex items-center justify-between mt-1">
                <div>
                  <h3 className={`text-sm font-extrabold ${dm ? "text-white" : "text-gray-900"}`}>
                    Portfolio Modules
                  </h3>
                  <p className={`text-xs ${dm ? "text-gray-400" : "text-gray-500"}`}>
                    Manage, analyze, and monitor your overall power plant portfolio across all assets.
                  </p>
                </div>
              </div>

              {/* Portfolio Module Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3.5">

                {/* Module Card 1 */}
                <div className={`p-4 rounded-xl border flex flex-col justify-between transition-all hover:shadow-md ${dm ? "bg-gray-800/90 border-gray-700 hover:border-[#0084CF]/50" : "bg-white border-[#DFE6EE] hover:border-[#0084CF]"}`}>
                  <div>
                    <div className="flex items-center justify-between mb-2.5">
                      <div className={`p-2 rounded-lg ${dm ? "bg-[#1C4D88]/40 text-[#3EA3DA]" : "bg-[#F2F8FC] text-[#0084CF]"}`}>
                        <TrendingUp size={18} />
                      </div>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${dm ? "bg-[#1C4D88]/40 text-[#8AC4E6]" : "bg-[#F2F8FC] text-[#1C4D88]"}`}>
                        Fleet Analytics
                      </span>
                    </div>
                    <h4 className={`axs-title mb-1 ${dm ? "text-white" : "text-gray-900"}`}>
                      Fleet Performance & Generation
                    </h4>
                    <p className={`text-xs leading-relaxed mb-3 ${dm ? "text-gray-400" : "text-gray-600"}`}>
                      Real-time fleet aggregation, generation heatmaps, plant-by-plant Performance Ratio (PR) rankings, and degradation tracking.
                    </p>
                  </div>
                  <div className="pt-3 border-t border-gray-700/40 flex items-center justify-between">
                    <span className="text-[11px] font-semibold text-gray-400">{portfolioMetrics.totalSites} Sites Monitored</span>
                    <button onClick={() => {
                      if (sites.length > 0) openSite(sites[0]);
                    }} className="inline-flex items-center gap-1 text-xs font-bold text-[#0084CF] hover:text-[#3EA3DA]">
                      Explore <ArrowUpRight size={13} />
                    </button>
                  </div>
                </div>

                {/* Module Card 2 */}
                <div className={`p-4 rounded-xl border flex flex-col justify-between transition-all hover:shadow-md ${dm ? "bg-gray-800/90 border-gray-700 hover:border-[#0066A3]/50" : "bg-white border-[#DFE6EE] hover:border-[#0066A3]"}`}>
                  <div>
                    <div className="flex items-center justify-between mb-2.5">
                      <div className={`p-2 rounded-lg ${dm ? "bg-[#0066A3]/30 text-[#8AC4E6]" : "bg-[#F2F8FC] text-[#0066A3]"}`}>
                        <Zap size={18} />
                      </div>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${dm ? "bg-[#0066A3]/30 text-[#C2DFF1]" : "bg-[#F2F8FC] text-[#0066A3]"}`}>
                        Virtual Power Plant
                      </span>
                    </div>
                    <h4 className={`axs-title mb-1 ${dm ? "text-white" : "text-gray-900"}`}>
                      VPP & Grid Dispatch Controller
                    </h4>
                    <p className={`text-xs leading-relaxed mb-3 ${dm ? "text-gray-400" : "text-gray-600"}`}>
                      Aggregate capacity for utility grid dispatch, demand response events, active power curtailment, and BESS storage optimization.
                    </p>
                  </div>
                  <div className="pt-3 border-t border-gray-700/40 flex items-center justify-between">
                    <span className="text-[11px] font-semibold text-gray-400">Capacity: {fmtNum(portfolioMetrics.totalAc, 1)} MW</span>
                    <button onClick={() => {
                      if (sites.length > 0) openSite(sites[0]);
                    }} className="inline-flex items-center gap-1 text-xs font-bold text-[#0084CF] hover:text-[#0066A3]">
                      Open VPP <ArrowUpRight size={13} />
                    </button>
                  </div>
                </div>

                {/* Module Card 3 */}
                <div className={`p-4 rounded-xl border flex flex-col justify-between transition-all hover:shadow-md ${dm ? "bg-gray-800/90 border-gray-700 hover:border-[#0084CF]/50" : "bg-white border-[#DFE6EE] hover:border-[#0084CF]"}`}>
                  <div>
                    <div className="flex items-center justify-between mb-2.5">
                      <div className={`p-2 rounded-lg ${dm ? "bg-[#0084CF]/30 text-[#8AC4E6]" : "bg-[#F2F8FC] text-[#0084CF]"}`}>
                        <DollarSign size={18} />
                      </div>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${dm ? "bg-[#0084CF]/30 text-[#C2DFF1]" : "bg-[#F2F8FC] text-[#0066A3]"}`}>
                        Commercial & Tariff
                      </span>
                    </div>
                    <h4 className={`axs-title mb-1 ${dm ? "text-white" : "text-gray-900"}`}>
                      Financials & PPA Billing Manager
                    </h4>
                    <p className={`text-xs leading-relaxed mb-3 ${dm ? "text-gray-400" : "text-gray-600"}`}>
                      Track PPA tariffs across portfolio assets, calculate daily revenue, monitor discom invoices, and analyze tariff compliance.
                    </p>
                  </div>
                  <div className="pt-3 border-t border-gray-700/40 flex items-center justify-between">
                    <span className="text-[11px] font-semibold text-gray-400">Avg Tariff: ₹{fmtNum(portfolioMetrics.avgTariff || 4.25, 2)}</span>
                    <button onClick={() => {
                      if (sites.length > 0) openSite(sites[0]);
                    }} className="inline-flex items-center gap-1 text-xs font-bold text-[#0084CF] hover:text-[#0066A3]">
                      View Revenue <ArrowUpRight size={13} />
                    </button>
                  </div>
                </div>

                {/* Module Card 4 */}
                <div className={`p-4 rounded-xl border flex flex-col justify-between transition-all hover:shadow-md ${dm ? "bg-gray-800/90 border-gray-700 hover:border-[#1C4D88]/50" : "bg-white border-[#DFE6EE] hover:border-[#1C4D88]"}`}>
                  <div>
                    <div className="flex items-center justify-between mb-2.5">
                      <div className={`p-2 rounded-lg ${dm ? "bg-[#1C4D88]/40 text-[#8AC4E6]" : "bg-[#F2F8FC] text-[#1C4D88]"}`}>
                        <Wrench size={18} />
                      </div>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${dm ? "bg-[#1C4D88]/40 text-[#C2DFF1]" : "bg-[#F2F8FC] text-[#1C4D88]"}`}>
                        Fleet Operations
                      </span>
                    </div>
                    <h4 className={`axs-title mb-1 ${dm ? "text-white" : "text-gray-900"}`}>
                      Outages & Maintenance Hub
                    </h4>
                    <p className={`text-xs leading-relaxed mb-3 ${dm ? "text-gray-400" : "text-gray-600"}`}>
                      Monitor forced outages, grid trippings, scheduled preventive maintenance (PM), inverter downtime, and technical work orders.
                    </p>
                  </div>
                  <div className="pt-3 border-t border-gray-700/40 flex items-center justify-between">
                    <span className="text-[11px] font-semibold text-gray-400">Uptime: 99.8%</span>
                    <button onClick={() => {
                      if (sites.length > 0) openSite(sites[0]);
                    }} className="inline-flex items-center gap-1 text-xs font-bold text-[#1C4D88] hover:text-[#0066A3]">
                      Open Maintenance <ArrowUpRight size={13} />
                    </button>
                  </div>
                </div>

                {/* Module Card 5 */}
                <div className={`p-4 rounded-xl border flex flex-col justify-between transition-all hover:shadow-md ${dm ? "bg-gray-800/90 border-gray-700 hover:border-[#1C4D88]/50" : "bg-white border-[#DFE6EE] hover:border-[#1C4D88]"}`}>
                  <div>
                    <div className="flex items-center justify-between mb-2.5">
                      <div className={`p-2 rounded-lg ${dm ? "bg-[#1C4D88]/40 text-[#8AC4E6]" : "bg-[#E3F0F9] text-[#1C4D88]"}`}>
                        <Database size={18} />
                      </div>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${dm ? "bg-[#1C4D88]/40 text-[#C2DFF1]" : "bg-[#E3F0F9] text-[#1C4D88]"}`}>
                        SCADA Logs
                      </span>
                    </div>
                    <h4 className={`axs-title mb-1 ${dm ? "text-white" : "text-gray-900"}`}>
                      Historian & Time-Series Engine
                    </h4>
                    <p className={`text-xs leading-relaxed mb-3 ${dm ? "text-gray-400" : "text-gray-600"}`}>
                      Query multi-year SCADA telemetry signals, solar irradiance logs, wind speed history, and export raw CSV/Parquet datasets.
                    </p>
                  </div>
                  <div className="pt-3 border-t border-gray-700/40 flex items-center justify-between">
                    <span className="text-[11px] font-semibold text-gray-400">10-sec High-Res Logs</span>
                    <button onClick={() => {
                      if (sites.length > 0) openSite(sites[0]);
                    }} className="inline-flex items-center gap-1 text-xs font-bold text-[#1C4D88] hover:text-[#8AC4E6]">
                      Access Historian <ArrowUpRight size={13} />
                    </button>
                  </div>
                </div>

                {/* Module Card 6 */}
                <div className={`p-4 rounded-xl border flex flex-col justify-between transition-all hover:shadow-md ${dm ? "bg-gray-800/90 border-gray-700 hover:border-[#0066A3]/50" : "bg-white border-[#DFE6EE] hover:border-[#0066A3]"}`}>
                  <div>
                    <div className="flex items-center justify-between mb-2.5">
                      <div className={`p-2 rounded-lg ${dm ? "bg-[#0066A3]/40 text-[#8AC4E6]" : "bg-[#E3F0F9] text-[#0066A3]"}`}>
                        <Cpu size={18} />
                      </div>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${dm ? "bg-[#0066A3]/40 text-[#C2DFF1]" : "bg-[#E3F0F9] text-[#0066A3]"}`}>
                        AI Intelligence
                      </span>
                    </div>
                    <h4 className={`axs-title mb-1 ${dm ? "text-white" : "text-gray-900"}`}>
                      AI Predictive Asset Health
                    </h4>
                    <p className={`text-xs leading-relaxed mb-3 ${dm ? "text-gray-400" : "text-gray-600"}`}>
                      Machine learning anomaly detection, inverter thermal stress scoring, transformer degradation alerts, and failure prediction.
                    </p>
                  </div>
                  <div className="pt-3 border-t border-gray-700/40 flex items-center justify-between">
                    <span className="text-[11px] font-semibold text-gray-400">Health Index: 97.2%</span>
                    <button onClick={() => {
                      if (sites.length > 0) openSite(sites[0]);
                    }} className="inline-flex items-center gap-1 text-xs font-bold text-[#0066A3] hover:text-[#8AC4E6]">
                      Launch Health AI <ArrowUpRight size={13} />
                    </button>
                  </div>
                </div>

              </div>
            </div>
          ) : (
            <>
              {/* Table scroll */}
              <div data-tour="ss-table" className="flex-1 overflow-y-auto overflow-x-auto">
                <table className="w-full border-collapse" style={{ minWidth: 860 }}>

                  {/* THEAD */}
                  <thead>
                    <tr>
                      {cols.map((col) => (
                        <th key={col.key}
                          style={{ minWidth: col.minW }}
                          className={`px-3 py-2.5 text-left text-[11px] font-extrabold uppercase tracking-wide whitespace-nowrap border-b border-r select-none ${col.kind === "static"
                              ? dm ? "bg-[#1C4D88] text-[#C2DFF1] border-[#0066A3]"
                                : "bg-[#0084CF] text-white border-[#0066A3]"
                              : dm ? "bg-[#0066A3]/70 text-white border-[#0066A3]"
                                : "bg-[#8AC4E6] text-[#0D2747] border-[#3EA3DA]"
                            }`}>
                          {col.header}
                        </th>
                      ))}
                    </tr>
                    {/* Units sub-header row */}
                    <tr>
                      {cols.map((col) => {
                        const unitMap: Record<string, string> = {
                          capacity_mwp: "MWp",
                          capacity_mw: "MW",
                          ppa_tariff: "₹/kWh",
                          peak_power: "kW",
                          today_energy: "kWh",
                          today_input: "kWh/m²",
                          plf: "%",
                          pr: "%",
                        };
                        const unit = unitMap[col.key] ?? "";
                        return (
                          <th key={col.key}
                            style={{ minWidth: col.minW }}
                            className={`px-3 py-1 text-left text-[11px] font-extrabold whitespace-nowrap border-b border-r select-none ${col.kind === "static"
                                ? dm ? "bg-[#1C4D88]/80 text-[#C2DFF1] border-[#0066A3]"
                                  : "bg-[#0066A3] text-white border-[#0084CF]"
                                : dm ? "bg-[#0066A3]/50 text-white border-[#0066A3]"
                                  : "bg-[#C2DFF1] text-[#0D2747] border-[#8AC4E6]"
                              }`}>
                            {unit}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>

                  {/* TBODY */}
                  <tbody ref={tbodyRef}>
                    {filtered.length === 0 ? (
                      <tr>
                        <td colSpan={cols.length} className="py-20 text-center">
                          <MapPin size={36} className={`mx-auto mb-3 ${dm ? "text-gray-600" : "text-gray-300"}`} />
                          <p className={`font-semibold ${dm ? "text-gray-400" : "text-gray-500"}`}>
                            {searchQ ? "No sites match your search." : "No active sites available."}
                          </p>
                        </td>
                      </tr>
                    ) : (
                      filtered.map((site, idx) => {
                        const isActive = activeSite === site.site_id;
                        return (
                          <motion.tr
                            key={site.site_id}
                            data-sid={site.site_id}
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: Math.min(idx * 0.025, 0.4) }}
                            onClick={() => setActiveSite(site.site_id)}
                            className={`cursor-pointer border-b transition-colors duration-100 ${isActive
                                ? dm ? "bg-[#1C4D88]/60" : "bg-[#F2F8FC]/70"
                                : dm ? "bg-gray-800/80 hover:bg-gray-700/80" : "bg-white/80 hover:bg-[#F6F8FB]/80"
                              } ${dm ? "border-gray-700/60" : "border-[#EEF2F7]/60"} backdrop-blur-sm`}
                          >
                            {cols.map((col) => (
                              <td key={col.key}
                                className={`px-3 py-2 text-sm border-r ${col.kind === "dynamic"
                                    ? dm ? "bg-[#1C4D88]/30 border-[#1C4D88]/20 text-[#8AC4E6] font-bold"
                                      : "bg-[#F2F8FC] border-[#C2DFF1]/60 text-[#0D2747] font-bold"
                                    : dm ? "border-gray-700/50 font-semibold text-gray-200" : "border-[#EEF2F7]/50 font-semibold text-gray-900"
                                  } backdrop-blur-sm`}>
                                {col.key === "power_plant" ? (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActiveSite(site.site_id);
                                      openSite(site);
                                    }}
                                    className="w-full text-left power-plant-btn"
                                    title={`Open ${site.site_name}`}
                                  >
                                    {col.render(site)}
                                  </button>
                                ) : (
                                  col.render(site)
                                )}
                              </td>
                            ))}
                          </motion.tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Bottom legend bar */}
              <div className={`flex-shrink-0 flex items-center gap-5 border-t px-4 py-2 text-[11px] font-medium ${dm ? "bg-gray-800 border-gray-700 text-gray-400" : "bg-[#F6F8FB] border-[#DFE6EE] text-gray-500"}`}>
                {[
                  { color: "#f59e0b", label: "Solar" },
                  { color: "#10b981", label: "Wind" },
                  { color: "#1C4D88", label: "Hybrid" },
                  { color: "#0084CF", label: "Battery-Hydro" },
                ].map(({ color, label }) => (
                  <span key={label} className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: color }} />
                    {label}
                  </span>
                ))}
                <span className="sync-text ml-auto flex items-center gap-1">
                  <RefreshCw size={9} className="animate-spin opacity-60" />
                  Live data syncing
                </span>
                <span className="map-instruction-text flex items-center gap-1">
                  <ArrowRight size={9} />
                  Click row to focus map, click Power Plant to open site
                </span>
              </div>
            </>
          )}
        </div>

        {/* ════ MAP PANEL ═════════════════════════════════════════ */}
        <div data-tour="ss-map" className={`flex flex-col border overflow-hidden rounded-xl shadow-lg transition-all duration-300 ${dm ? "border-gray-700 bg-gray-800 shadow-black/40" : "border-[#DFE6EE] bg-white shadow-slate-200/80"}`}
          style={{ width: "28%", minWidth: 320, display: viewMode === "sites" ? "flex" : "none" }}>

          {/* Map title bar */}
          <div className={`flex-shrink-0 flex items-center justify-between gap-2 px-4 h-11 border-b ${dm ? "bg-gray-800 border-gray-700" : "bg-white border-[#DFE6EE]"}`}>
            <div className="flex items-center gap-2">
              <MapPin size={14} className="text-[#0084CF]" />
              <span className={`text-sm font-extrabold ${dm ? "text-white" : "text-gray-900"}`}>Site Locations</span>
            </div>
          </div>

          {/* Map */}
          <div className="flex-1 relative overflow-hidden" style={{ minHeight: 300 }}>
            {/* Leaflet container */}
            <div ref={mapDivRef} className="absolute inset-0 w-full h-full" id="site-map-root" />

            {/* Leaflet loading */}
            {!leafletOk && (
              <div className={`absolute inset-0 flex flex-col items-center justify-center gap-2 z-10 ${dm ? "bg-gray-900" : "bg-gray-100"}`}>
                <Loader2 size={30} className="animate-spin text-[#0084CF]" />
                <p className={`text-xs font-medium ${dm ? "text-gray-400" : "text-gray-500"}`}>Loading map…</p>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default SiteSelection;
